import React from "react";
import styled from "@emotion/styled";
import { App } from "antd5";

import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import { useAddOpportunityEntity } from "lib/hooks/api/opportunities/useAddOpportunityEntity";
import { useRemoveOpportunityEntity } from "lib/hooks/api/opportunities/useRemoveOpportunityEntity";
import { useSubscription } from "lib/hooks/subscription";
import { OpportunityEntityType } from "../../../lib/types/graphQLEnums";
import { Flex } from "../../../styles/utility-components";
import ActionToast from "../../ui/action_toast/ActionToast";
import { convertEntityTypeToOpportunityEntityType } from "../saving/types";
import { Entities, entityToDataType } from "./utils";

type EntityCardProps = {
  header?: React.ReactNode;
  entity: Entities;
  entityId: string;
  entityType: OpportunityEntityType;
  entityName: string;
  opportunityId: string;
  children: React.ReactNode;
  cta?: React.ReactNode;
};

export default function EntityCardContent({
  header,
  entity,
  entityId,
  entityType,
  entityName,
  opportunityId,
  children,
  cta,
}: EntityCardProps) {
  const { message } = App.useApp();

  const subscription = useSubscription();
  const hasAccess = subscription.hasDataTypes(entityToDataType(entity));

  const { mutate: addEntity } = useAddOpportunityEntity({
    onSuccess: () => {
      message.open({
        icon: null,
        content: <ActionToast title={entityName} subtitle="saved to this opportunity" />,
      });
    },
    onError: () => {
      message.error(`Failed to undo ${entityType} from this opportunity`);
    },
  });

  const { mutate: removeEntity } = useRemoveOpportunityEntity({
    onSuccess: () => {
      message.open({
        icon: null,
        content: (
          <ActionToast
            title={entityName}
            subtitle="unsaved from this opportunity"
            secondaryAction={{
              label: "Undo",
              onClick: () => {
                addEntity({
                  id: opportunityId,
                  entities: {
                    entities: [
                      {
                        entityId,
                        entityType: convertEntityTypeToOpportunityEntityType(
                          entityType,
                          OpportunityEntityType,
                        ),
                      },
                    ],
                  },
                });
              },
            }}
          />
        ),
      });
    },
    onError: () => {
      message.error(`Failed to remove ${entityType} from this opportunity`);
    },
  });

  return (
    <Container column gap={16}>
      <Flex justifyContent="space-between" gap={16}>
        {header && <Flex>{header}</Flex>}
        <Flex gap={8}>
          {cta}
          {hasAccess && (
            <DropdownMenu
              trigger={
                <StyledDropdownMenuTrigger
                  buttonStyle="inverse"
                  buttonType="primary"
                  icon="dotsVertical"
                  aria-label="Entity actions"
                  hideArrow
                />
              }
              width={129}
            >
              <DropdownMenuItem
                key="unsave"
                icon="bookmark"
                iconProps={{
                  fill: "none",
                }}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  removeEntity({
                    id: opportunityId,
                    entities: {
                      entities: [
                        {
                          entityId: entityId,
                          entityType: convertEntityTypeToOpportunityEntityType(
                            entityType,
                            OpportunityEntityType,
                          ),
                        },
                      ],
                    },
                  });
                }}
              >
                Unsave
              </DropdownMenuItem>
            </DropdownMenu>
          )}
        </Flex>
      </Flex>
      {children}
    </Container>
  );
}

const Container = styled(Flex)({
  width: "100%",
  justifyContent: "space-between",
});

const StyledDropdownMenuTrigger = styled(DropdownMenuTrigger)({
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: "transparent",
  },
});
